import { defineRule } from 'vee-validate';
import { string, ValidationError, AnySchema, number } from 'yup';
import { differenceInDays, format, isAfter, isBefore, isValid, parse } from 'date-fns';
import isEmail from 'validator/es/lib/isEmail';

import { i18n } from '@/plugins/i18n';

import { CustomPlugin } from '../types/index';

export type GlobalRuleName =
  | 'required'
  | 'email'
  | 'minLength'
  | 'url'
  | 'tel'
  | ('date' | `date:${string},${string}`)
  | 'time'
  | ('weight' | `weight:${number},${number}`);

const { t } = i18n.global;

const wrapYupValidationToRule = (schema: AnySchema) => (value: unknown) => {
  try {
    schema.validateSync(value);

    return true;
  } catch (error) {
    if (!(error instanceof ValidationError)) throw error;

    return error.errors[0];
  }
};

export const setGlobalRules = () => {
  defineRule('required', wrapYupValidationToRule(string().required()));
  defineRule('email', (value: string) => {
    if (!isEmail(value)) {
      return t('validation.string.email');
    }

    return true;
  });

  defineRule(
    'tel',
    wrapYupValidationToRule(string().matches(/[(][1-9]{2}[)] \d{5}-\d{4}/g, t('validation.tel'))),
  );

  defineRule('date', (value: string, [min, max]: [string?, string?]) => {
    if (!value) return true;

    if (value.length !== 10) {
      return t('validation.date.short');
    }

    const now = new Date();
    const date = parse(value, 'dd/MM/yyyy', now);

    if (!isValid(date)) {
      return t('validation.date.invalid');
    }

    if (min) {
      const minDate = parse(min, 'yyyy-MM-dd', now);

      if (isBefore(date, minDate)) {
        return t('validation.date.min', { min: differenceInDays(now, minDate) });
      }
    }

    if (max) {
      const maxDate = parse(max, 'yyyy-MM-dd', now);

      if (isAfter(date, maxDate)) {
        return t('validation.date.max', { max: format(maxDate, 'dd/MM/yyyy') });
      }
    }

    return true;
  });

  defineRule('time', (value: string) => {
    if (!value) return true;

    const [hour, minute] = value.split(':');

    return (parseInt(hour, 10) < 24 && parseInt(minute, 10) < 60) || t('validation.time.invalid');
  });

  defineRule('weight', (value: unknown, [min, max]: [number?, number?]) => {
    const schema = number()
      .transform((_, num: string | number) => {
        const strNum = String(num);

        return strNum.includes('.') || i18n.global.locale.value !== 'ptBR'
          ? Number(strNum)
          : Number(strNum.replace(/,/, '.'));
      })
      .min(min || 0, t('validation.weight.min', { min }))
      .max(max || 1000, t('validation.weight.max', { max }));

    return wrapYupValidationToRule(schema)(value);
  });
};

const plugin: CustomPlugin = {
  install: setGlobalRules,
};

export default plugin;
